import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Clock, CheckCircle, XCircle, Plus, Minus, Home, TreePine, ChefHat, Sparkles, Truck, Check, Trash2, Printer, Volume2, VolumeX, Receipt } from "lucide-react";
import api, { formatPrice, getStatusLabel, getStatusColor, getNextOrderStatus, isSuperadmin } from "@/lib/api";

const STATUS_ICONS = {
  new: Clock, accepted: Check, preparing: ChefHat, ready: Sparkles, delivered: Truck
};
const NEXT_LABEL = {
  new: "Принять", accepted: "Готовить", preparing: "Готово", ready: "Доставлено"
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const canDelete = isSuperadmin();
  const [activeTab, setActiveTab] = useState("kitchen"); // "kitchen" | "services"
  const [activeZone, setActiveZone] = useState(() => localStorage.getItem("eco_orders_zone") || "house"); // "house" | "letnik"
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("eco_sound_enabled") !== "0");
  const [autoPrint, setAutoPrint] = useState(() => localStorage.getItem("eco_autoprint") !== "0");
  const [lastReceipt, setLastReceipt] = useState(null);
  const [closedSessions, setClosedSessions] = useState([]);
  const [showClosed, setShowClosed] = useState(false);
  const [openVenueDialog, setOpenVenueDialog] = useState(false);
  const [openForm, setOpenForm] = useState({ venue_id: "", extra_adults: 0, extra_children: 0 });
  const seenOrdersRef = useRef(new Set());
  const audioCtxRef = useRef(null);

  // Play 2-tone beep using Web Audio API (no external file needed)
  const playBeep = () => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      // Resume context if suspended (browser autoplay policy)
      if (ctx.state === "suspended") ctx.resume();

      const playTone = (freq, startTime, duration) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = freq;
        g.gain.setValueAtTime(0.0001, startTime);
        g.gain.exponentialRampToValueAtTime(0.4, startTime + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);
        o.connect(g); g.connect(ctx.destination);
        o.start(startTime); o.stop(startTime + duration);
      };
      const t0 = ctx.currentTime;
      playTone(880, t0, 0.18);
      playTone(1320, t0 + 0.2, 0.25);
    } catch (e) { /* ignore */ }
  };

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem("eco_sound_enabled", next ? "1" : "0");
    if (next) playBeep(); // confirm sound + unlock audio context on user gesture
  };

  const toggleAutoPrint = () => {
    const next = !autoPrint;
    setAutoPrint(next);
    localStorage.setItem("eco_autoprint", next ? "1" : "0");
  };

  // Print via hidden iframe — popup blockers can't kill it
  const printReceipt = (url) => {
    const frame = document.createElement("iframe");
    frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    frame.src = url;
    document.body.appendChild(frame);
    setTimeout(() => frame.remove(), 60000);
  };

  // Manual order state
  const [createOpen, setCreateOpen] = useState(false);
  const [zoneType, setZoneType] = useState("house");
  const [houses, setHouses] = useState([]);
  const [venues, setVenues] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [services, setServices] = useState([]);
  const [createTab, setCreateTab] = useState("menu"); // "menu" | "services"
  const [selectedPlaceId, setSelectedPlaceId] = useState("");
  const [cart, setCart] = useState([]);
  const [orderForm, setOrderForm] = useState({ name: "", phone: "", notes: "" });
  const [creating, setCreating] = useState(false);
  const [createSearch, setCreateSearch] = useState("");
  const [openCats, setOpenCats] = useState({});

  const load = (silent = false) => {
    if (!silent) setLoading(true);
    api.get("/admin/orders").then(r => {
      const data = Array.isArray(r.data) ? r.data : r.data?.orders || [];

      // Detect new orders (compare with seen set, but only after first load)
      if (seenOrdersRef.current.size > 0) {
        const newOnes = data.filter(o => !seenOrdersRef.current.has(o.id) && o.status === "new");
        if (newOnes.length > 0) {
          if (soundOn) playBeep();
          if (autoPrint) newOnes.forEach(o => printReceipt(`/admin/print/order/${o.id}`));
        }
      }
      seenOrdersRef.current = new Set(data.map(o => o.id));

      setOrders(data);
    }).catch(() => {}).finally(() => { if (!silent) setLoading(false); });
    api.get("/admin/sessions/open").then(r => setSessions(r.data || [])).catch(() => {});
  };

  const closeSession = async (s) => {
    const parts = [];
    if (s.bath_cost > 0) parts.push(`🛁 Домик (${s.bath_minutes} мин): ${s.bath_cost.toLocaleString()} тг`);
    if (s.extra_cost > 0) parts.push(`👥 Доп. гости: ${s.extra_cost.toLocaleString()} тг`);
    parts.push(`📋 Заказы: ${s.orders_total.toLocaleString()} тг`);
    parts.push(`💰 ИТОГО: ${s.total.toLocaleString()} тг`);
    if (!window.confirm(`Закрыть ${s.venue_name}?\n\n${parts.join("\n")}`)) return;
    try {
      const r = await api.post("/admin/sessions/close", { venue_type: s.venue_type, venue_id: s.venue_id });
      const url = `/admin/print/session/${r.data.session_id}`;
      printReceipt(url);
      setLastReceipt({ url, venue_name: s.venue_name, total: r.data.total, session_id: r.data.session_id });
      load();
      loadClosed();
    } catch (e) {
      alert(e.response?.data?.detail || "Ошибка закрытия");
    }
  };

  const loadClosed = () => {
    api.get("/admin/sessions/closed?limit=50").then(r => setClosedSessions(r.data || [])).catch(() => {});
  };

  const openVenue = async () => {
    if (!openForm.venue_id) { alert("Выберите домик"); return; }
    try {
      await api.post("/admin/sessions/open", {
        venue_type: activeZone === "house" ? "house" : (venues.find(v => v.id === openForm.venue_id)?.venue_type || "table"),
        venue_id: openForm.venue_id,
        extra_adults: Number(openForm.extra_adults) || 0,
        extra_children: Number(openForm.extra_children) || 0,
      });
      setOpenVenueDialog(false);
      setOpenForm({ venue_id: "", extra_adults: 0, extra_children: 0 });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Не удалось открыть место");
    }
  };

  const updateGuests = async (s, field, delta) => {
    const next = {
      extra_adults: s.extra_adults || 0,
      extra_children: s.extra_children || 0,
    };
    next[field] = Math.max(0, (next[field] || 0) + delta);
    try {
      await api.put(`/admin/sessions/${s.venue_id}/guests`, next);
      load(true);
    } catch (e) {
      alert(e.response?.data?.detail || "Сначала откройте место кнопкой «Открыть домик»");
    }
  };

  useEffect(() => {
    load();
    loadClosed();
    api.get("/admin/houses").then(r => setHouses(r.data)).catch(() => api.get("/houses").then(r => setHouses(r.data)).catch(() => {}));
    api.get("/admin/venues").then(r => setVenues(r.data)).catch(() => {});
    api.get("/menu").then(r => setMenuItems(r.data)).catch(() => {});
    api.get("/admin/services").then(r => setServices(r.data.filter(s => s.is_active !== false))).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling for new orders every 15s, sessions every 30s (bath cost ticks)
  useEffect(() => {
    const id = setInterval(() => load(true), 15000);
    return () => clearInterval(id);
  }, [soundOn, autoPrint]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (id, status) => {
    await api.put(`/admin/orders/${id}/status`, { status });
    load();
  };

  const deleteOrder = async (id) => {
    if (!window.confirm("Удалить этот заказ безвозвратно?")) return;
    try {
      await api.delete(`/admin/orders/${id}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Ошибка удаления");
    }
  };

  const clearHistory = async () => {
    const onlyDelivered = window.confirm(
      "OK — очистить только доставленные/отменённые заказы.\nОтмена — выбрать другой режим."
    );
    let url = "/admin/orders/clear";
    if (onlyDelivered) {
      // Two calls: delivered + cancelled
      try {
        const r1 = await api.delete(`${url}?status=delivered`);
        const r2 = await api.delete(`${url}?status=cancelled`);
        alert(`Удалено: ${(r1.data.deleted || 0) + (r2.data.deleted || 0)}`);
        load();
      } catch (e) {
        alert(e.response?.data?.detail || "Ошибка");
      }
      return;
    }
    if (!window.confirm(`Удалить ВСЕ ${orders.length} заказов? Действие необратимо.`)) return;
    if (!window.confirm("Подтвердите ещё раз — удалить ВСЕ заказы?")) return;
    try {
      const r = await api.delete(url);
      alert(`Удалено заказов: ${r.data.deleted}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Ошибка");
    }
  };

  // Cart helpers
  const addItem = (item, type) => {
    const existing = cart.find(c => c.id === item.id && c.item_type === type);
    if (existing) setCart(cart.map(c => (c.id === item.id && c.item_type === type) ? { ...c, qty: c.qty + 1 } : c));
    else setCart([...cart, { id: item.id, item_type: type, name: item.name_ru || item.name_kz || "", price: item.price, qty: 1 }]);
  };
  const removeItem = (id, type) => setCart(cart.filter(c => !(c.id === id && c.item_type === type)));
  const updateQty = (id, type, d) => setCart(cart.map(c => (c.id === id && c.item_type === type) ? { ...c, qty: Math.max(1, c.qty + d) } : c));
  const cartTotal = cart.reduce((s, c) => s + c.price * c.qty, 0);

  const handleCreate = async () => {
    if (!selectedPlaceId || cart.length === 0) {
      alert("Выберите место и добавьте хотя бы одну позицию");
      return;
    }
    setCreating(true);
    const isHouse = zoneType === "house";
    const venue = isHouse ? houses.find(h => h.id === selectedPlaceId) : venues.find(v => v.id === selectedPlaceId);
    const venueType = isHouse ? "house" : (venue?.venue_type || "table");
    const venueName = venue ? (venue.name_ru || venue.name_kz || "") : "";

    const items = cart.map(c => {
      const base = { name: c.name, price: c.price, quantity: c.qty, item_type: c.item_type };
      if (c.item_type === "menu") base.menu_item_id = c.id;
      else base.service_id = c.id;
      return base;
    });

    try {
      await api.post("/admin/orders/create", {
        house_id: isHouse ? selectedPlaceId : "",
        venue_type: venueType,
        venue_id: selectedPlaceId,
        venue_name: venueName,
        items,
        client_name: orderForm.name.trim() || `Гость ${venueName}`,
        client_phone: orderForm.phone.trim(),
        notes: orderForm.notes
      });
      setCreateOpen(false);
      setCart([]);
      setOrderForm({ name: "", phone: "", notes: "" });
      setSelectedPlaceId("");
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Ошибка создания заказа");
    }
    setCreating(false);
  };

  const getVenueName = (order) => order.venue_name || order.house_name_ru || order.house_name_kz || order.house_id?.slice(0, 8) || "—";
  const getVenueIcon = (order) => {
    const vt = order.venue_type || "house";
    if (vt === "house") return <Home className="w-3 h-3 text-primary" />;
    return <TreePine className="w-3 h-3 text-green-400" />;
  };

  // Filter orders by zone (house vs letnik) and tab (kitchen vs services)
  const inZone = (o) => {
    const vt = o.venue_type || "house";
    return activeZone === "house" ? vt === "house" : (vt === "tapshan" || vt === "table");
  };
  const tabFilter = activeTab === "kitchen" ? "menu" : "service";
  const visibleOrders = orders
    .filter(inZone)
    .map(o => {
      // Items relevant to this tab. Treat unknown item_type as "menu" for backwards compat.
      const items = (o.items || []).filter(it => (it.item_type || "menu") === tabFilter);
      return { ...o, _filtered_items: items };
    })
    .filter(o => o._filtered_items.length > 0);

  // Filter sessions by zone too
  const visibleSessions = sessions.filter(s => activeZone === "house" ? s.venue_type === "house" : (s.venue_type === "tapshan" || s.venue_type === "table"));

  const setZone = (z) => {
    setActiveZone(z);
    localStorage.setItem("eco_orders_zone", z);
  };

  return (
    <div data-testid="admin-orders">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl">Заказы</h1>
        <div className="flex items-center gap-2">
          <Button onClick={toggleSound} variant="outline" className={`rounded-none gap-1.5 text-xs ${soundOn ? "border-primary/30 text-primary" : "border-white/10 text-muted-foreground"}`} data-testid="sound-toggle" title={soundOn ? "Звук включён" : "Звук выключен"}>
            {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
            {soundOn ? "Звук" : "Без звука"}
          </Button>
          <Button onClick={toggleAutoPrint} variant="outline" className={`rounded-none gap-1.5 text-xs ${autoPrint ? "border-primary/30 text-primary" : "border-white/10 text-muted-foreground"}`} data-testid="autoprint-toggle" title="Автопечать чека при новом заказе">
            <Printer className="w-3.5 h-3.5" />
            {autoPrint ? "Автопечать вкл" : "Автопечать выкл"}
          </Button>
          {canDelete && (
            <Button onClick={clearHistory} variant="outline" className="rounded-none border-red-500/30 text-red-400 hover:bg-red-500/10 gap-1.5 text-xs" data-testid="clear-orders-btn">
              <Trash2 className="w-3.5 h-3.5" /> Очистить историю
            </Button>
          )}
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground rounded-none gap-2" data-testid="create-order-btn">
              <Plus className="w-4 h-4" /> Создать заказ
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif">Новый заказ</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Zone type */}
              <div className="flex gap-2">
                <Button size="sm" variant={zoneType === "house" ? "default" : "outline"} onClick={() => { setZoneType("house"); setSelectedPlaceId(""); }}
                  className="rounded-none gap-1 flex-1" data-testid="create-zone-house">
                  <Home className="w-3 h-3" /> Банные дома
                </Button>
                <Button size="sm" variant={zoneType === "letnik" ? "default" : "outline"} onClick={() => { setZoneType("letnik"); setSelectedPlaceId(""); }}
                  className="rounded-none gap-1 flex-1" data-testid="create-zone-letnik">
                  <TreePine className="w-3 h-3" /> Летник
                </Button>
              </div>

              {/* Place selector */}
              <Select value={selectedPlaceId} onValueChange={setSelectedPlaceId}>
                <SelectTrigger className="bg-background border-white/10 rounded-none" data-testid="create-place-select"><SelectValue placeholder="Выберите место" /></SelectTrigger>
                <SelectContent className="bg-card border-white/10 max-h-60">
                  {zoneType === "house" ? (
                    houses.map(h => <SelectItem key={h.id} value={h.id}>{h.name_ru || h.name_kz}</SelectItem>)
                  ) : (
                    venues.map(v => <SelectItem key={v.id} value={v.id}>{v.name_ru || v.name_kz} ({v.venue_type === "tapshan" ? "Тапчан" : "Столик"})</SelectItem>)
                  )}
                </SelectContent>
              </Select>

              {/* Client info — optional */}
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Имя (опц.)" value={orderForm.name} onChange={e => setOrderForm({...orderForm, name: e.target.value})} className="bg-background border-white/10 rounded-none" data-testid="create-name" />
                <Input placeholder="Телефон (опц.)" value={orderForm.phone} onChange={e => setOrderForm({...orderForm, phone: e.target.value})} className="bg-background border-white/10 rounded-none" data-testid="create-phone" />
              </div>
              <Input placeholder="Примечание" value={orderForm.notes} onChange={e => setOrderForm({...orderForm, notes: e.target.value})} className="bg-background border-white/10 rounded-none" />

              {/* Item type tabs (menu vs services) */}
              <Tabs value={createTab} onValueChange={setCreateTab}>
                <TabsList className="bg-background border border-white/10 rounded-none w-full">
                  <TabsTrigger value="menu" className="rounded-none flex-1" data-testid="create-tab-menu">Кухня и бар</TabsTrigger>
                  <TabsTrigger value="services" className="rounded-none flex-1" data-testid="create-tab-services">Услуги</TabsTrigger>
                </TabsList>
              </Tabs>

              <div>
                <p className="text-xs text-muted-foreground mb-2">{createTab === "menu" ? "Добавить блюда:" : "Добавить услуги:"}</p>
                <Input
                  placeholder="Поиск по названию..."
                  value={createSearch}
                  onChange={(e) => setCreateSearch(e.target.value)}
                  className="bg-background border-white/10 rounded-none h-8 text-xs mb-2"
                  data-testid="create-item-search"
                />
                <div className="max-h-60 overflow-y-auto border border-white/5 p-1">
                  {(() => {
                    const source = createTab === "menu" ? menuItems : services;
                    const getCatLabel = (it) => createTab === "menu"
                      ? (it.category_ru || it.category_kz || "Без категории")
                      : ({peeling:"Пилинг",steaming:"Парение",massage:"Массаж",spa_program:"СПА программа",package:"Пакеты",accessories_sale:"Продажа",accessories_rental:"Прокат"}[it.category] || "Прочее");
                    const q = createSearch.trim().toLowerCase();
                    const filtered = q
                      ? source.filter(i => ((i.name_ru || "") + " " + (i.name_kz || "")).toLowerCase().includes(q))
                      : source;
                    // Group
                    const groups = {};
                    for (const it of filtered) {
                      const cat = getCatLabel(it);
                      (groups[cat] = groups[cat] || []).push(it);
                    }
                    const cats = Object.keys(groups).sort();
                    if (cats.length === 0) return <p className="text-xs text-muted-foreground text-center py-4">Ничего не найдено</p>;
                    return cats.map((cat, ci) => {
                      // When searching, auto-expand. Otherwise use openCats state.
                      const expanded = q ? true : (openCats[cat] !== false && (openCats[cat] || ci === 0));
                      return (
                        <div key={cat} className="mb-1" data-testid={`create-cat-${cat}`}>
                          <button
                            type="button"
                            onClick={() => setOpenCats(s => ({ ...s, [cat]: !expanded }))}
                            className="w-full flex items-center justify-between px-2 py-1.5 bg-card hover:bg-card/80 text-xs font-medium border-b border-white/5"
                          >
                            <span className="flex items-center gap-1">
                              <span className="text-primary">{expanded ? "▾" : "▸"}</span>
                              {cat}
                            </span>
                            <span className="text-muted-foreground text-[10px]">({groups[cat].length})</span>
                          </button>
                          {expanded && (
                            <div className="pl-2 pt-0.5">
                              {groups[cat].map(item => (
                                <div key={item.id} className="flex items-center justify-between py-1 px-1 hover:bg-background/50">
                                  <span className="text-xs flex-1 truncate">
                                    {item.name_ru || item.name_kz} — <span className="text-primary">{formatPrice(item.price)}</span>
                                    {createTab === "services" && item.category === "package" && <span className="text-[9px] text-primary ml-1">[Пакет]</span>}
                                  </span>
                                  <Button size="icon" variant="ghost" onClick={() => addItem(item, createTab === "menu" ? "menu" : "service")} className="w-6 h-6" data-testid={`create-add-${item.id}`}>
                                    <Plus className="w-3 h-3" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Cart */}
              {cart.length > 0 && (
                <div className="border border-primary/20 p-3 space-y-2">
                  <p className="text-xs text-primary uppercase tracking-widest mb-2">Корзина</p>
                  {cart.map(c => (
                    <div key={`${c.item_type}-${c.id}`} className="flex items-center justify-between text-sm">
                      <span className="flex-1 truncate">
                        {c.name}
                        <span className="text-[10px] text-muted-foreground ml-1">[{c.item_type === "menu" ? "Кухня" : "Услуга"}]</span>
                      </span>
                      <div className="flex items-center gap-1">
                        <button onClick={() => updateQty(c.id, c.item_type, -1)} className="w-5 h-5 border border-white/10 flex items-center justify-center"><Minus className="w-2.5 h-2.5" /></button>
                        <span className="w-4 text-center text-xs">{c.qty}</span>
                        <button onClick={() => updateQty(c.id, c.item_type, 1)} className="w-5 h-5 border border-white/10 flex items-center justify-center"><Plus className="w-2.5 h-2.5" /></button>
                        <button onClick={() => removeItem(c.id, c.item_type)} className="text-red-400 ml-1"><XCircle className="w-3.5 h-3.5" /></button>
                      </div>
                      <span className="text-xs text-primary ml-2 w-16 text-right">{formatPrice(c.price * c.qty)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between pt-2 border-t border-white/10 font-medium">
                    <span>Итого</span>
                    <span className="text-primary">{formatPrice(cartTotal)}</span>
                  </div>
                </div>
              )}

              <Button onClick={handleCreate} disabled={creating} className="w-full bg-primary text-primary-foreground rounded-none" data-testid="create-submit">
                {creating ? "Создание..." : "Создать заказ"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Zone tabs: Banya houses vs Letnik */}
      <div className="flex gap-1 mb-4 border-b border-white/10" data-testid="orders-zone-tabs">
        <button
          onClick={() => setZone("house")}
          className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 transition-colors ${activeZone === "house" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          data-testid="zone-tab-house"
        >
          <Home className="w-4 h-4" /> Банные дома
          {orders.filter(o => (o.venue_type || "house") === "house").length > 0 && <span className="text-[10px] bg-white/10 px-1.5 rounded">{orders.filter(o => (o.venue_type || "house") === "house").length}</span>}
        </button>
        <button
          onClick={() => setZone("letnik")}
          className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 transition-colors ${activeZone === "letnik" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          data-testid="zone-tab-letnik"
        >
          <TreePine className="w-4 h-4" /> Летник
          {orders.filter(o => o.venue_type === "tapshan" || o.venue_type === "table").length > 0 && <span className="text-[10px] bg-white/10 px-1.5 rounded">{orders.filter(o => o.venue_type === "tapshan" || o.venue_type === "table").length}</span>}
        </button>
      </div>

      {/* Last closed receipt banner (в случае, если печать заблокирована браузером) */}
      {lastReceipt && (
        <div className="mb-4 bg-primary/10 border border-primary/30 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="last-receipt-banner">
          <span className="text-sm">
            Чек закрыт: <b>{lastReceipt.venue_name}</b> — {formatPrice(lastReceipt.total)}
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => printReceipt(lastReceipt.url)} className="bg-primary text-primary-foreground rounded-none gap-1 text-xs" data-testid="reprint-last-btn">
              <Printer className="w-3 h-3" /> Печать
            </Button>
            <a href={lastReceipt.url} target="_blank" rel="noreferrer" className="text-xs underline text-primary" data-testid="open-last-receipt-link">Открыть чек</a>
            <button onClick={() => setLastReceipt(null)} className="text-muted-foreground text-xs" data-testid="dismiss-receipt-banner">Скрыть</button>
          </div>
        </div>
      )}

      {/* Open a venue (starts the time tariff immediately) */}
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <Dialog open={openVenueDialog} onOpenChange={setOpenVenueDialog}>
          <DialogTrigger asChild>
            <Button variant="outline" className="rounded-none gap-2 border-primary/30 text-primary text-xs" data-testid="open-venue-btn">
              <Home className="w-3.5 h-3.5" /> {activeZone === "house" ? "Открыть домик" : "Открыть место"}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-white/10 max-w-md">
            <DialogHeader><DialogTitle className="font-serif">{activeZone === "house" ? "Открыть домик" : "Открыть место"}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-xs text-muted-foreground">
                Время начинает идти сразу. Первый час оплачивается полностью, далее — поминутно.
              </p>
              <Select value={openForm.venue_id} onValueChange={(v) => setOpenForm({ ...openForm, venue_id: v })}>
                <SelectTrigger className="bg-background border-white/10 rounded-none" data-testid="open-venue-select"><SelectValue placeholder="Выберите место" /></SelectTrigger>
                <SelectContent className="bg-card border-white/10 max-h-60">
                  {(activeZone === "house" ? houses : venues).map(v => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.name_ru || v.name_kz}{v.price_per_hour ? ` — ${formatPrice(v.price_per_hour)}/час` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Доп. взрослых (2500/час)</label>
                  <Input type="number" min="0" value={openForm.extra_adults}
                    onChange={e => setOpenForm({ ...openForm, extra_adults: e.target.value.replace(/^0+(?=\d)/, "") })}
                    className="bg-background border-white/10 rounded-none" data-testid="open-extra-adults" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Доп. детей (1000/час)</label>
                  <Input type="number" min="0" value={openForm.extra_children}
                    onChange={e => setOpenForm({ ...openForm, extra_children: e.target.value.replace(/^0+(?=\d)/, "") })}
                    className="bg-background border-white/10 rounded-none" data-testid="open-extra-children" />
                </div>
              </div>
              <Button onClick={openVenue} className="w-full bg-primary text-primary-foreground rounded-none" data-testid="open-venue-submit">
                Открыть
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Button variant="outline" onClick={() => { setShowClosed(!showClosed); loadClosed(); }} className="rounded-none gap-2 text-xs" data-testid="toggle-closed-sessions">
          <Receipt className="w-3.5 h-3.5" /> Закрытые чеки ({closedSessions.length})
        </Button>
      </div>

      {/* Closed receipts history */}
      {showClosed && (
        <div className="mb-6 bg-card border border-white/10 p-3" data-testid="closed-sessions">
          <h2 className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Закрытые чеки</h2>
          {closedSessions.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="closed-sessions-empty">Пока нет закрытых чеков</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {closedSessions.map(cs => (
                <div key={cs.id} className="flex items-center justify-between gap-3 border-b border-white/5 pb-2" data-testid={`closed-session-${cs.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm truncate">{cs.venue_name || "—"}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(cs.closed_at).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      {cs.bath_minutes ? ` · ${cs.bath_minutes} мин` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-primary text-sm">{formatPrice(cs.total_amount || 0)}</span>
                    <Button size="sm" variant="outline" onClick={() => printReceipt(`/admin/print/session/${cs.id}`)} className="rounded-none gap-1 text-xs" data-testid={`reprint-${cs.id}`}>
                      <Printer className="w-3 h-3" /> Печать
                    </Button>
                    <a href={`/admin/print/session/${cs.id}`} target="_blank" rel="noreferrer" className="text-xs underline text-muted-foreground hover:text-primary">открыть</a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Open sessions (places with unpaid orders) */}
      {visibleSessions.length > 0 && (
        <div className="mb-6" data-testid="open-sessions">
          <h2 className="text-xs uppercase tracking-widest text-primary mb-3">Открытые места ({visibleSessions.length})</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {visibleSessions.map(s => {
              const minutes = s.bath_minutes || 0;
              const hh = Math.floor(minutes / 60), mm = minutes % 60;
              const timeLabel = hh > 0 ? `${hh}ч ${mm}м` : `${mm}м`;
              return (
                <div key={`${s.venue_type}-${s.venue_id}`} className="bg-card border border-primary/30 p-3 flex flex-col gap-2" data-testid={`session-${s.venue_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        {s.venue_type === "house" ? <Home className="w-3.5 h-3.5 text-primary" /> : <TreePine className="w-3.5 h-3.5 text-green-400" />}
                        <span className="font-serif truncate">{s.venue_name}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {s.orders.length} заказ(ов)
                        {s.opened_at ? ` · открыт в ${new Date(s.opened_at).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}` : ""}
                      </p>
                      {s.venue_type === "house" && s.bath_cost > 0 && (
                        <p className="text-xs text-amber-500 mt-0.5">Домик {timeLabel} — {formatPrice(s.bath_cost)}</p>
                      )}
                      {s.extra_cost > 0 && (
                        <p className="text-xs text-amber-500">Доп. гости — {formatPrice(s.extra_cost)}</p>
                      )}
                      <p className="text-primary font-serif text-lg mt-0.5">{formatPrice(s.total)}</p>
                    </div>
                    <Button size="sm" onClick={() => closeSession(s)} className="bg-primary text-primary-foreground rounded-none gap-1 text-xs" data-testid={`close-session-${s.venue_id}`}>
                      <Receipt className="w-3 h-3" /> Закрыть
                    </Button>
                  </div>

                  {s.venue_type === "house" && (
                    <div className="flex items-center gap-4 text-xs border-t border-white/10 pt-2">
                      <div className="flex items-center gap-1.5">
                        <span className="text-muted-foreground">Взр.+</span>
                        <button onClick={() => updateGuests(s, "extra_adults", -1)} className="w-5 h-5 border border-white/10 flex items-center justify-center" data-testid={`adults-minus-${s.venue_id}`}><Minus className="w-2.5 h-2.5" /></button>
                        <span className="w-4 text-center" data-testid={`adults-count-${s.venue_id}`}>{s.extra_adults || 0}</span>
                        <button onClick={() => updateGuests(s, "extra_adults", 1)} className="w-5 h-5 border border-white/10 flex items-center justify-center" data-testid={`adults-plus-${s.venue_id}`}><Plus className="w-2.5 h-2.5" /></button>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-muted-foreground">Дет.+</span>
                        <button onClick={() => updateGuests(s, "extra_children", -1)} className="w-5 h-5 border border-white/10 flex items-center justify-center" data-testid={`children-minus-${s.venue_id}`}><Minus className="w-2.5 h-2.5" /></button>
                        <span className="w-4 text-center" data-testid={`children-count-${s.venue_id}`}>{s.extra_children || 0}</span>
                        <button onClick={() => updateGuests(s, "extra_children", 1)} className="w-5 h-5 border border-white/10 flex items-center justify-center" data-testid={`children-plus-${s.venue_id}`}><Plus className="w-2.5 h-2.5" /></button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Main tabs: Kitchen vs Services */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="bg-card border border-white/10 rounded-none">
          <TabsTrigger value="kitchen" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground gap-2" data-testid="orders-tab-kitchen">
            <ChefHat className="w-4 h-4" /> Кухня и бар
          </TabsTrigger>
          <TabsTrigger value="services" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground gap-2" data-testid="orders-tab-services">
            <Sparkles className="w-4 h-4" /> Услуги
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <p className="text-muted-foreground">Загрузка...</p>
      ) : visibleOrders.length === 0 ? (
        <p className="text-muted-foreground" data-testid="orders-empty">Заказов нет</p>
      ) : (
        <div className="space-y-3">
          {visibleOrders.map(order => {
            const StatusIcon = STATUS_ICONS[order.status] || Clock;
            const nextStatus = getNextOrderStatus(order.status);
            return (
              <div key={order.id} className="bg-card border border-white/5 p-4" data-testid={`order-${order.id}`}>
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <div className="flex items-center gap-1.5">
                    {getVenueIcon(order)}
                    <span className="font-serif">{getVenueName(order)}</span>
                  </div>
                  <span className={`text-xs px-2 py-0.5 flex items-center gap-1 ${getStatusColor(order.status)}`}>
                    <StatusIcon className="w-3 h-3" /> {getStatusLabel(order.status)}
                  </span>
                  {order.source === "admin" && <span className="text-[10px] px-1.5 py-0.5 bg-blue-900/30 text-blue-400">Ручной</span>}
                  <span className="text-xs text-muted-foreground ml-auto">{new Date(order.created_at).toLocaleString("ru")}</span>
                </div>
                <div className="text-sm mb-2">
                  <span className="text-muted-foreground">{order.client_name || "—"}</span>
                  {order.client_phone && <span className="text-muted-foreground ml-3">{order.client_phone}</span>}
                </div>
                <div className="flex flex-wrap gap-1 mb-3">
                  {order._filtered_items.map((item, i) => {
                    const pkgName = item.package_name_ru || item.package_name_kz;
                    const itemKey = `${order.id}-${item.menu_item_id || item.service_id || item.name}-${i}`;
                    return (
                      <span key={itemKey} className="text-xs bg-muted px-2 py-0.5">
                        {item.name || item.menu_item_id?.slice(0,8) || item.service_id?.slice(0,8)} x{item.quantity}
                        {pkgName && <span className="text-primary ml-1">({pkgName})</span>}
                      </span>
                    );
                  })}
                </div>
                {order.notes && <p className="text-xs text-muted-foreground mb-3 italic">{order.notes}</p>}
                <div className="flex items-center justify-between">
                  <span className="text-primary font-serif">{formatPrice(order.total_amount)}</span>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" onClick={() => printReceipt(`/admin/print/order/${order.id}`)} variant="outline" className="rounded-none gap-1 text-xs" data-testid={`print-${order.id}`} title="Печать чека">
                      <Printer className="w-3 h-3" /> Печать
                    </Button>
                    {nextStatus && (
                      <Button size="sm" onClick={() => updateStatus(order.id, nextStatus)} className="bg-primary/15 text-primary hover:bg-primary/25 rounded-none gap-1 text-xs" data-testid={`advance-${order.id}`}>
                        <CheckCircle className="w-3 h-3" /> {NEXT_LABEL[order.status]}
                      </Button>
                    )}
                    {order.status !== "delivered" && order.status !== "cancelled" && (
                      <Button size="sm" onClick={() => updateStatus(order.id, "cancelled")} variant="outline" className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 rounded-none gap-1 text-xs" data-testid={`cancel-${order.id}`}>
                        <XCircle className="w-3 h-3" /> Отменить
                      </Button>
                    )}
                    <Button size="sm" onClick={() => deleteOrder(order.id)} variant="outline" className={`border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-none gap-1 text-xs ${canDelete ? "" : "hidden"}`} data-testid={`delete-${order.id}`}>
                      <Trash2 className="w-3 h-3" /> Удалить
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
